import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get OAuth code and state from URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        // Handle OAuth error
        if (error) {
          setError(`OAuth error: ${error}`);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate code exists
        if (!code) {
          setError('Missing authorization code');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate CSRF state parameter
        const storedState = sessionStorage.getItem('oauth_state');
        if (!storedState || state !== storedState) {
          setError('Invalid state parameter (CSRF protection)');
          sessionStorage.removeItem('oauth_state');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Clear state after validation
        sessionStorage.removeItem('oauth_state');

        // Exchange code for session token
        // In a real implementation, this would call the backend API
        // For now, we'll simulate a successful OAuth flow for testing

        // Simulate API call to exchange code for token
        const response = await fetch('/api/auth/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state }),
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to exchange code for token');
        }

        const data = await response.json();

        // Store session token
        sessionStorage.setItem('partner_session', data.session_token || 'mock-session-token');

        // Fetch user data
        await refetchUser();

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err: unknown) {
        console.error('OAuth callback error:', err);
        setError((err as Error).message || 'Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, refetchUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#282828] via-[#4c1d95] to-[#282828]">
        <div className="max-w-md w-full space-y-8 p-10 bg-[#282828]/80 backdrop-blur-sm rounded-2xl border border-red-500/30 shadow-2xl">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl text-[#F8F3EC] mb-4">Authentication Error</h2>
            <p className="text-sm text-white/60 mb-6">{error}</p>

            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 bg-[#1283EB] hover:bg-[#1283EB]/90 text-white font-medium rounded-lg transition-colors"
            >
              Back to Login
            </button>

            <p className="text-xs text-white/40 mt-4">Auto-redirecting in 3 seconds...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#282828] via-[#4c1d95] to-[#282828]">
      <div className="max-w-md w-full space-y-8 p-10 bg-[#282828]/80 backdrop-blur-sm rounded-2xl border border-[#1283EB]/30 shadow-2xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1283EB] mx-auto mb-4"></div>
          <h2 className="text-xl text-[#F8F3EC]">Completing sign in...</h2>
          <p className="text-sm text-white/60 mt-2">Please wait while we authenticate you</p>
        </div>
      </div>
    </div>
  );
}

export default AuthCallback;
